import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useCookies } from 'react-cookie';
import { checkoutCountryList as countryList } from 'shared/data/siteSettings';
import { Cart as CartType } from 'shared/types/cart';
import Message from 'components/commercetools-ui/atoms/message';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import useCloseFlyouts from 'helpers/hooks/useCloseFlyouts';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useMobileDevice from 'helpers/hooks/useMobileDevice';
import aggregatedQuantity from 'helpers/utils/aggregatedQuantity';
import { useCart, useAccount, useStock } from 'frontastic';
import CartContent from './components/cart-content';
import { CartProps } from './types';
import OrderSummary from '../order-summary';
import CheckoutButton from '../order-summary/components/checkout-button';
import { CheckoutButtonProps } from '../order-summary/types';
import { CLICKED_OBJECT_ID, IS_SEARCH, SEARCH_QUERY_ID } from 'helpers/constants/algolia';

const Cart: React.FC<CartProps> = ({ paymentMethods, ...props }) => {
  const { itemsWithStockStatus, isStockChecked, triggerGetStock, fluentStocks } = useStock();
  const { data, isEmpty, productSKUs, cartRecalculate, hasExcludedItem, hasItemsBelowMinQty } = useCart();
  triggerGetStock(productSKUs as string[]);
  const { locale } = useLocale();
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { isMobileDevice } = useMobileDevice();

  const closeFlyouts = useCloseFlyouts();

  const { loggedIn, account } = useAccount();
  const [isLoading, setIsLoading] = useState(true);
  const [initCartRecalculate, setInitCartRecalculate] = useState(true);
  const searchParams = useSearchParams();

  const errorId = searchParams.get('errorId');

  useEffect(() => {
    const triggerCartRecalculate = async () => {
      setInitCartRecalculate(false);
      cartRecalculate();
    };

    if (((data && data?.lineItems?.length) || 0) > 0 && initCartRecalculate) {
      triggerCartRecalculate();
    }
    sessionStorage?.removeItem(SEARCH_QUERY_ID);
    sessionStorage?.removeItem(CLICKED_OBJECT_ID);
    sessionStorage?.removeItem(IS_SEARCH);
  }, [data?.lineItems]);

  useEffect(() => {
    setTimeout(() => {
      if (isLoading) {
        setIsLoading(!isLoading);
      }
    }, 3000);
  }, []);

  const cartItemsWithStockStatus = itemsWithStockStatus(data as CartType);
  const [cookies, _] = useCookies(['locale_selection']);
  const countryCookie = cookies?.locale_selection ? cookies?.locale_selection['countryCode'] : 'GB';
  const isCountryCodePresent = (countryCode: string) => {
    return countryList.some((country) => country.countryCode === countryCode);
  };
  const isCountrySupported = isCountryCodePresent(countryCookie);
  const hasUnavailableItems = useMemo(() => {
    return data?.lineItems?.some((item) => {
      if (item?.isPerpetualStock) {
        return;
      }
      const availableQuantity = aggregatedQuantity(item.variant?.sku, fluentStocks);
      const hasInsufficientStocks = item.count && item.count > availableQuantity;
      const hasOutOfStockItems = availableQuantity === 0;
      return hasOutOfStockItems || hasInsufficientStocks || hasExcludedItem || hasItemsBelowMinQty;
    });
  }, [data?.lineItems, fluentStocks]);

  const hasDiscontinuedItems = useMemo(() => {
    return data?.lineItems?.some((item) => item?.variant?.attributes?.isDiscontinued);
  }, [data?.lineItems]);

  const defaultCheckoutButtonProps: CheckoutButtonProps = useMemo(() => {
    return {
      text: formatCartMessage({ id: 'checkout.go', defaultMessage: 'Go to checkout' }),
      link: '/checkout',
      onClick: closeFlyouts,
      disabled: isEmpty || hasUnavailableItems || hasDiscontinuedItems || errorId == 'error04' || !isCountrySupported,
      isLoading: !isStockChecked,
    };
  }, [closeFlyouts, formatCartMessage, hasUnavailableItems, isEmpty, isStockChecked, hasDiscontinuedItems]);

  const hasInitializedDataLayer = useRef(false);
  useEffect(() => {
    const currentUrl = document.location.origin;
    if (!hasInitializedDataLayer.current && data !== undefined) {
      const userEmail = loggedIn ? account?.email : '';
      const salutation = loggedIn ? account?.salutation || '' : '';
      const dataLayer = new DataLayerHelpers(currentUrl, locale, data, userEmail, account?.accountId, salutation);

      const dataLayerData = {
        breadcrumb: 'Checkout / View Cart',
        event: 'checkout',
        step: 1,
        option: 'CartView',
      };
      dataLayer.setCartDataLayer(dataLayerData);
      hasInitializedDataLayer.current = true;
    }
  }, [data, loggedIn, account, locale]);

  return (
    <div className="relative bg-neutral-200">
      {errorId == 'error03' && (
        <div className="mx-16 pt-24 lg:m-auto lg:w-[70%]">
          <Message
            severity={'error'}
            message={formatCartMessage({
              id: 'applePay.shipping.error',
              defaultMessage:
                "Unfortunately, your cart can't be fulfilled to your chosen location at this time. Please change your delivery address to continue.",
            })}
          ></Message>
        </div>
      )}

      <div
        className={`flex flex-col lg:flex-row lg:items-start lg:gap-26 lg:px-20 lg:py-48 xl:px-48 ${
          errorId == 'error03' ? '!pt-16' : ''
        }`}
      >
        <CartContent
          className="bg-white px-16 py-12 md:px-24 md:py-12 lg:min-h-[400px] lg:w-[70%] lg:rounded-md lg:px-20 lg:py-36 xl:px-48"
          cartItems={cartItemsWithStockStatus}
          isLoading={isLoading}
          {...props}
          isStockChecked={productSKUs.length !== 0 && isStockChecked && fluentStocks.length !== 0}
          errorId={errorId ? errorId : ''}
        />

        <OrderSummary
          className="bg-white px-16 pb-12 pt-24 uppercase md:px-24 md:py-12 lg:mt-0 lg:w-[30%] lg:rounded-md lg:p-36 lg:px-20 lg:pb-44 xl:px-48"
          title={formatCartMessage({ id: 'order.summary', defaultMessage: 'Order Summary' })}
          includeLoginSuggestion
          isLoading={isLoading}
          // paymentMethods={paymentMethods}
          button={
            !isMobileDevice && !isEmpty ? (
              <CheckoutButton className="hidden md:block" {...defaultCheckoutButtonProps} />
            ) : (
              <></>
            )
          }
        />
      </div>
      {isMobileDevice && !isEmpty && (
        <CheckoutButton
          className="sticky bottom-0 w-full border-t border-neutral-400 bg-white p-16 md:hidden"
          {...defaultCheckoutButtonProps}
        />
      )}
    </div>
  );
};

export default Cart;
